import { faArrowUp, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faLock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, Skeleton } from '@mui/material';
import { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { toast } from 'react-toastify';

import { AnimatedTransition } from '../../../../../components/animated-transition';
import Input from '../../../../../components/input/input';
import {
  OTC_TRADER_DELTA_LIST,
  OTC_TRADER_TENOR_LIST,
} from '../../../../../constants/reports/reports-filters';
import { SurfaceMarkingContext } from '../../../../../providers/otc-trader/surface-marking-provider';
import { WebSocketContext } from '../../../../../providers/websocket-context/websocket-provider';
import Utils from '../../../../../utils/immersive_utils';
import { VolatilitySurfaceConfirmModal } from '../../../../../pages/otc-trader/components/volatility-surface-confirm-modal';
import {
  deltaParameters,
  getDeltaFromSurface,
  transformData,
} from '../../../../../pages/otc-trader/utils/calculate-conversion';

import './styles.scss';

type RowData = {
  tenor: string;
  [key: string]: string;
};

export const VolatilitySurfaceMarkingTable = () => {
  const {
    volatilityOptions: { surface, tenor, delta, tokenPair },
    options: { deltaOption, tenorOption },
    volatilityData,
    getAllListRateSymbols,
    isCreateUserVolSurfaceLoading,
    surfaceLoading,
    volatilityTableData,
    setVolatilityTableData,
    volatilityTableValidationErrors,
    setVolatilityTableValidationErrors,
    setVolatilitySavedTableData,
    isLoadingTransformation,
    setIsLoadingTransformation,
    volatilitySavedTableData,
    savedDelta,
  } = useContext(SurfaceMarkingContext);

  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);

  const isChange = useRef(false);

  const [overrideDelta, setOverrideDelta] = useState<string>('');
  const [overrideItems, setOverrideItems] = useState<RowData[]>([]);
  const [isRemovingOverrides, setIsRemovingOverrides] = useState(false);

  const isCurrentDeltaOverride = useMemo(
    () => overrideDelta === '' || overrideDelta === delta.id,
    [delta, overrideDelta],
  );

  const getCalculation = async (vol_surface: any): Promise<RowData[]> => {
    let attempts = 0;
    while (isLoadingTransformation && attempts < 3) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    setIsLoadingTransformation(true);

    return new Promise((resolve, reject) => {
      const payload = {
        jsonrpc: '2.0',
        id: `CALCULATION_SURFACE_MARKING`,
        method: 'transform_vol_surface',
        params: { vol_surface },
      };

      sendMessage(JSON.stringify(payload));

      subscribeToChannel(`CALCULATION_SURFACE_MARKING`, (response: any) => {
        if (!response || !response.result || !response.result['vol_surface']) {
          setIsLoadingTransformation(false);
          return;
        }

        const result = response.result['vol_surface'];

        try {
          const newObject: RowData[] = Object.entries(result).map(([tenor, data]) => {
            return { tenor, ...(data as any) };
          });

          resolve(newObject);
        } catch (error) {
          setIsLoadingTransformation(false);
          reject(error);
        } finally {
          setIsLoadingTransformation(false);
        }
      });
    });
  };

  const [isOpenSaveNewSurfaceModal, setIsOpenSaveNewSurfaceModal] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const isSurfaceOptionEmpty = !tokenPair;
  const deltaIdPrevious = useRef<string | null>(null);

  useEffect(() => {
    if (surfaceLoading || !tenor) return;

    isChange.current = true;

    setVolatilityTableData((prevData) => {
      const prevDataMap = new Map(prevData.map((row) => [row.tenor, row]));

      const updatedData: RowData[] = tenor.map((selectedTenor) => {
        const existingRow = prevDataMap.get(selectedTenor);

        if (existingRow) {
          return existingRow;
        }

        const newRow: RowData = { tenor: selectedTenor };

        delta?.value?.forEach((deltaKey: any) => {
          const surfaceValue = surface?.vols?.find(
            (vol: any) => vol.tenor === selectedTenor && vol.tag === deltaKey,
          );

          newRow[deltaKey] = surfaceValue ? (Number(surfaceValue.value) * 100).toString() : '';
        });

        return newRow;
      });

      updatedData.sort((a, b) => {
        const positionA = OTC_TRADER_TENOR_LIST.find((item) => item.value === a.tenor)?.position;
        const positionB = OTC_TRADER_TENOR_LIST.find((item) => item.value === b.tenor)?.position;
        return (positionA || 0) - (positionB || 0);
      });

      return updatedData;
    });
  }, [tenor]);

  const performTransformations = (data?: RowData[], deltaId?: string) => {
    const currentDelta = deltaId || delta.id;

    const transformedData: any = data || volatilityTableData;
    const vol_surface = transformData(transformedData, currentDelta);

    getCalculation(vol_surface).then((response: any) => {
      if (response) {
        const roundedData = response.map((obj: any) => {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]: any) =>
              key === 'tenor' ? [key, value] : [key, `${Utils.roundPrecision(value || 0, 2)}`],
            ),
          );
        });

        const rewriteRow = (current: RowData, newValues: RowData, currentKeys: string[]) => {
          const result = { ...current };

          for (const key in newValues) {
            if (!currentKeys.includes(key)) {
              result[key] = newValues[key];
            }
          }

          return result;
        };

        isChange.current = true;
        setVolatilityTableData((prev) => {
          const newObj = prev.map((row) => {
            const newRow = roundedData.find((item: any) => item.tenor === row.tenor);
            return rewriteRow(row, newRow, deltaParameters[currentDelta]);
          });

          return newObj;
        });
        setIsRemovingOverrides(false);
      }
    });
  };

  useEffect(() => {
    if (surfaceLoading) return;

    deltaIdPrevious.current = delta.id;
  }, [delta]);

  const updateSelectedDelta = (deltaId: string) => {
    const deltaFound = OTC_TRADER_DELTA_LIST.find((item) => item.id === deltaId);

    if (deltaFound) {
      deltaOption.handleChange([deltaFound]);
    }
  };

  useEffect(() => {
    if (surfaceLoading) return;

    if (surface?.vols?.length) {
      isChange.current = true;

      const deltaId = getDeltaFromSurface(surface);

      if (deltaId) {
        updateSelectedDelta(deltaId);
      }

      const extractedDeltas = Array.from(new Set(surface.vols.map((vol: any) => vol.tag)));
      const extractedTenors = Array.from(new Set(surface.vols.map((vol: any) => vol.tenor)));

      const updatedTableData: RowData[] = extractedTenors.map((tenor) => {
        const row: RowData = { tenor };

        extractedDeltas.forEach((deltaKey) => {
          const surfaceValue = surface.vols.find(
            (vol: any) => vol.tenor === tenor && vol.tag === deltaKey,
          )?.value;
          row[deltaKey] = surfaceValue ? (Number(surfaceValue) * 100).toFixed(2) : '';
        });

        return row;
      });

      updatedTableData.sort((a, b) => {
        const positionA = OTC_TRADER_TENOR_LIST.find((item) => item.value === a.tenor)?.position;
        const positionB = OTC_TRADER_TENOR_LIST.find((item) => item.value === b.tenor)?.position;
        return (positionA || 0) - (positionB || 0);
      });

      setVolatilityTableData(updatedTableData);
      setVolatilitySavedTableData(JSON.parse(JSON.stringify(updatedTableData)));
      setVolatilityTableValidationErrors({});
      setOverrideDelta('');
      setOverrideItems([]);
      performTransformations(updatedTableData, deltaId);
    } else {
      const defaultTenors = ['1W', '2W'];
      const defaultDeltas = ['ATMF', '10P', '25P', '25C', '10C'];

      const defaultTableData: RowData[] = defaultTenors.map((tenor) => {
        const row: RowData = { tenor };
        defaultDeltas.forEach((deltaKey: any) => {
          row[deltaKey] = '';
        });
        return row;
      });

      setVolatilityTableData(defaultTableData);
      setVolatilitySavedTableData(JSON.parse(JSON.stringify(defaultTableData)));
      setVolatilityTableValidationErrors({});
      setOverrideDelta('');
      setOverrideItems([]);
    }
  }, [surface, surfaceLoading]);

  useEffect(() => {
    if (!volatilityData.currentUserVolSurfaceId) return;
    const symbol = tokenPair;

    getAllListRateSymbols(symbol);
  }, [volatilityData.currentUserVolSurfaceId]);

  useEffect(() => {
    if (!volatilityTableData || !delta?.value) {
      setIsSaveDisabled(true);
      return;
    }

    const filledRowsExist = volatilityTableData?.some((row) =>
      delta.value.some((key: any) => row[key] !== ''),
    );
    setIsSaveDisabled(!filledRowsExist);
  }, [volatilityTableData, delta]);

  const handleInputChange = (rowIndex: number, key: string, value: string, tenor: string) => {
    const updatedData = [...volatilityTableData];
    updatedData[rowIndex][key] = value;

    setVolatilityTableData(updatedData);

    if (!isChange.current) {
      setOverrideDelta(delta.id);
      setOverrideItems((prev) => {
        const find = prev.find((item) => item.tenor === tenor && item.key === key);

        if (!find) {
          return [...prev, { tenor, key }];
        }

        return prev;
      });
      performTransformations(updatedData, delta.id);
    }
  };

  const validateRows = useCallback(
    (deltaId: string) => {
      const errors: { [tenor: string]: string[] } = {};

      volatilityTableData.forEach((row) => {
        const filledFields = Object.keys(row)?.filter((key) => key !== 'tenor' && row[key]);
        if (filledFields.length > 0 && filledFields.length < delta?.value?.length) {
          errors[row.tenor] = delta?.value?.filter((delta: any) => !row[delta]);
        }
      });

      setVolatilityTableValidationErrors((prevErrors) => ({
        ...prevErrors,
        [deltaId]: errors,
      }));
      return Object.keys(errors)?.length === 0;
    },
    [volatilityTableData, delta],
  );

  const handleOpenSaveSurfaceModal = useCallback(() => {
    if (!validateRows(delta.id)) return;

    setIsOpenSaveNewSurfaceModal(true);
  }, [volatilityTableData]);

  const handleCloseSaveSurfaceModal = () => {
    setIsOpenSaveNewSurfaceModal(false);
  };

  useEffect(() => {
    if (!volatilityTableData) return;
  }, [volatilityTableData]);

  const getFilteredDataForModal = (): any => {
    const defineDataDecimals = (key: string) => {
      if (key === '10/25RR' || key === '10/25FLY') {
        return 8;
      }

      return 6;
    };

    const convertedVolatility = volatilityTableData.flatMap((row) =>
      Object.keys(row)
        ?.filter((key) => key !== 'tenor' && row[key])
        ?.map((key) => ({
          tenor: row.tenor,
          tag: key,
          value: (Number(row[key]) / 100).toFixed(defineDataDecimals(key)),
        })),
    );

    const paramArr = deltaParameters[overrideDelta || delta.id];
    const filtered = convertedVolatility.filter((vol) => paramArr?.includes(vol.tag));

    return filtered;
  };

  const handleRemoveOverrides = () => {
    const tenorsSaved = Array.from(new Set(volatilitySavedTableData.map((row) => row.tenor)));

    const tenorList = OTC_TRADER_TENOR_LIST.filter((tenorList) =>
      tenorsSaved.includes(tenorList.value),
    );
    tenorOption.handleChange(tenorList);

    setVolatilityTableData(volatilitySavedTableData);
    setOverrideDelta('');
    setOverrideItems([]);
    setIsRemovingOverrides(true);

    performTransformations(volatilitySavedTableData, savedDelta);
  };

  const renderSkeleton = useMemo(() => {
    return <Skeleton variant="text" height={40} animation="wave" sx={{ bgcolor: '#416077' }} />;
  }, []);

  const renderInput = (rowIndex: number, tag: string, row: RowData) => {
    const hasError = volatilityTableValidationErrors[delta.id]?.[row.tenor]?.includes(tag);

    const overrideLocked = overrideDelta !== '' && overrideDelta !== delta.id;
    const isOverride = overrideItems.some((item) => item.tenor === row.tenor && item.key === tag);

    const handleUnlockOverride = () => {
      toast.info(
        <div className="toast-message">
          <span>Save or remove existing overrides to edit the current view.</span>
          <Button onClick={() => toast.dismiss()} className="ok-toast-button">
            OK
          </Button>
        </div>,
        {
          position: 'bottom-left',
          type: 'error',
          autoClose: false,
          closeOnClick: false,
          style: { width: '400px' },
          pauseOnHover: true,
          draggable: false,
        },
      );
    };

    const getInputClassName = () => {
      const classNameArr: string[] = ['editable-input'];

      if (hasError) {
        classNameArr.push('input-error');
      }

      if (isOverride) {
        classNameArr.push('override-input');
      }

      return classNameArr.join(' ');
    };

    let inputProps: NumericFormatProps = {
      value: row[tag] || '',
      onValueChange: (values: any) => {
        const { value } = values;

        handleInputChange(rowIndex, tag, value, row.tenor);
      },
      decimalScale: 2,
      fixedDecimalScale: true,
      className: getInputClassName(),
      placeholder: '-',
      customInput: Input,
      onKeyDown: (e: any) => {
        const notNum =
          isNaN(e?.key) && e?.key !== 'Backspace' && e?.key !== 'Delete' && e?.key !== '.';

        if (!notNum) {
          isChange.current = false;
        }
      },
    };

    if (!['10/25RR', '10/25FLY'].includes(tag)) {
      inputProps = {
        ...inputProps,
        suffix: '%',
      };
    }

    if (isCreateUserVolSurfaceLoading || surfaceLoading) {
      return renderSkeleton;
    }

    if (overrideLocked) {
      if (isRemovingOverrides) {
        return renderSkeleton;
      }

      return (
        <AnimatedTransition>
          <div className={'locked-input-container'}>
            <IconButton onClick={handleUnlockOverride} className="override-locked-icon">
              <FontAwesomeIcon icon={faLock} className="icon" />
            </IconButton>
            <div className="input-value">{`${Utils.roundPrecision(parseFloat(`${inputProps.value || 0}`), 2).toFixed(2)} ${inputProps.suffix || ''}`}</div>
          </div>
        </AnimatedTransition>
      );
    }

    return (
      <AnimatedTransition>
        <NumericFormat {...inputProps} />
      </AnimatedTransition>
    );
  };

  if (isSurfaceOptionEmpty) {
    return (
      <div className="vol-surface-empty">
        <span>Select a token to create the vol surface report</span>
      </div>
    );
  }

  return (
    <div className="volatility-surface-marking-table-container">
      <table className="editable-table">
        <thead>
          <tr>
            <th>Tenor</th>
            {delta?.value?.map((delta: string, index: number) => (
              <th key={index} {...Utils.obscuredProps}>
                {delta}
                <FontAwesomeIcon icon={faArrowUp} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {volatilityTableData.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td>{row.tenor}</td>
              {delta?.value?.map((deltaParam: any, deltaIndex: number) => (
                <td key={deltaIndex}>{renderInput(rowIndex, deltaParam, row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="save-button-area">
        {Object.keys(volatilityTableValidationErrors[delta.id] || {}).length > 0 && (
          <div className="save-button-area-message-error">
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>You need to add all the data in a row to save your surface</span>
          </div>
        )}

        {overrideDelta !== '' && (
          <Button onClick={handleRemoveOverrides} className="remove-overrides-button">
            Remove Overrides
          </Button>
        )}
        <Button
          onClick={handleOpenSaveSurfaceModal}
          className="save-button"
          disabled={isSaveDisabled || !isCurrentDeltaOverride}
        >
          Save
        </Button>
      </div>

      <VolatilitySurfaceConfirmModal
        open={isOpenSaveNewSurfaceModal}
        handleClose={handleCloseSaveSurfaceModal}
        dataVols={getFilteredDataForModal()}
      />
    </div>
  );
};
